import React, { useEffect, useMemo, useState } from 'react';
import { WaitlistRegistration } from '../../models/waitlistRegistration';
import { SystemAdminDataService } from '../../services/systemAdminDataService';

const fmt = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const WaitlistRegistrations: React.FC = () => {
  const [rows, setRows] = useState<WaitlistRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SystemAdminDataService.fetchWaitlistRegistrations();
      setRows(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load waitlist registrations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.name, r.email, r.org || '', r.size || ''].join(' ').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const SentBadge: React.FC<{ at: string | null; label: string }> = ({ at, label }) => (
    at ? (
      <span className="sys-state sys-state--on" title={`${label}: ${fmt(at)}`}>
        <i className="fas fa-check-circle" />{fmt(at)}
      </span>
    ) : (
      <span className="sys-state sys-state--off"><i className="fas fa-minus-circle" />Not sent</span>
    )
  );

  return (
    <div className="sys-page">
      <div className="sys-page__head">
        <p className="sys-page__eyebrow">Platform</p>
        <h1 className="sys-page__title">Waitlist</h1>
        <span className="sys-page__count">
          {loading ? 'Loading…'
            : visible.length === rows.length
              ? `${rows.length} ${rows.length === 1 ? 'registration' : 'registrations'}`
              : `${visible.length} of ${rows.length}`}
        </span>
      </div>

      <div className="pill-toolbar" role="toolbar" aria-label="Waitlist filters">
        <div className="pill-toolbar__search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search by name, email, organisation or size"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search waitlist registrations"
          />
        </div>
        <div className="pill-toolbar__spacer" />
        <button
          className="pill-toolbar__icon-btn"
          onClick={load}
          disabled={loading}
          title="Reload"
          aria-label="Reload waitlist registrations"
        >
          <i className="fas fa-sync-alt" style={loading ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
        </button>
      </div>

      <div className="sys-page__body">
        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : loading && rows.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : rows.length === 0 ? (
          // An empty screen is an invitation, not a dead end — say where the
          // rows will come from.
          <div className="ivory-panel sys-panel sys-panel--pad text-center py-5">
            <div className="sys-page__title mb-2">No registrations yet</div>
            <div className="sys-table__muted">
              Sign-ups from the rustleit.com waitlist form appear here as they arrive.
            </div>
          </div>
        ) : (
          <div className="ivory-panel sys-panel sys-panel--table">
            <div className="sys-table-scroll">
              <table className="sys-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Organisation</th>
                    <th>Children</th>
                    <th>Registered</th>
                    <th>Acknowledgement</th>
                    <th>Notification</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => (
                    <tr key={r.id}>
                      <td className="sys-table__id">{r.id}</td>
                      <td className="sys-table__name">{r.name}</td>
                      <td><a href={`mailto:${r.email}`}>{r.email}</a></td>
                      <td>{r.org || <span className="sys-table__muted">—</span>}</td>
                      <td className="sys-table__num">{r.size || <span className="sys-table__muted">—</span>}</td>
                      <td className="sys-table__muted" style={{ whiteSpace: 'nowrap' }}>{fmt(r.registered_at)}</td>
                      <td style={{ fontSize: '0.83rem' }}><SentBadge at={r.acknowledgement_sent_at} label="Acknowledgement sent" /></td>
                      <td style={{ fontSize: '0.83rem' }}><SentBadge at={r.notification_sent_at} label="Notification sent" /></td>
                    </tr>
                  ))}
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={8} className="sys-table__empty">No registrations match your search.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitlistRegistrations;
